import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface ApiSidebarItem {
  id: string;
  title: string;
  indent?: boolean;
}

export interface ApiSidebarSection {
  title: string;
  items: ApiSidebarItem[];
}

interface ApiSidebarProps {
  sections: ApiSidebarSection[];
  className?: string;
  onItemClick?: () => void;
}

export function ApiSidebar({ sections, className, onItemClick }: ApiSidebarProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      setActiveId(hash || null);
    };

    // Set initial active state
    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Also track scroll position for active state
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[id]');
      let currentActiveId: string | null = null;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        // Check if section is in upper portion of viewport
        if (rect.top <= 150 && rect.bottom >= 0) {
          currentActiveId = section.id;
        }
      });

      if (currentActiveId && currentActiveId !== activeId) {
        setActiveId(currentActiveId);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeId]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      window.history.replaceState(null, '', `#${id}`);
      setActiveId(id);
      onItemClick?.();
    }
  };

  return (
    <aside className={cn('w-64 shrink-0', className)}>
      <div className="sticky top-24">
        <ScrollArea className="h-[calc(100vh-8rem)]">
          <nav className="space-y-6">
            {sections.map((section) => (
              <div key={section.title}>
                <h4 className="font-semibold text-sm mb-2">{section.title}</h4>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={cn(
                          'w-full text-left text-sm py-1.5 px-2 rounded-md transition-colors',
                          'hover:bg-muted hover:text-foreground',
                          item.indent && 'pl-4 text-xs',
                          activeId === item.id
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground'
                        )}
                      >
                        {item.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </ScrollArea>
      </div>
    </aside>
  );
}
